// 🔧 seo_head.js
// ✅ Purpose: Reusable SEO Helmet component for public and event pages
// ✅ Features:
// - Uses centralized SEO config or dynamic page values
// - Adds title, description, canonical URL, Open Graph, Twitter card, and robots metadata
// - Supports noindex for private or event-specific pages when needed

import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

const SITEWIDE_PATH = (path) => path === "/" || path === "/help" || path === "/guides" || path.startsWith("/guides/") || ["/solutions", "/how-it-works", "/about", "/contact", "/terms", "/privacy", "/car-shows", "/art-shows", "/pet-shows", "/school-elections", "/silent-auctions", "/qr-code-voting", "/peoples-choice-voting", "/ranked-choice-voting", "/judged-scorecards", "/volunteer-management", "/sponsor-management"].includes(path);

const SeoHead = ({
  title,
  description,
  path = "",
  image = "https://letsgovote.app/images/social-share-default.png",
  keywords = "",
  noindex = false,
  structuredData = null,
  breadcrumbs = [],
}) => {
  const [siteSeo, setSiteSeo] = useState(null);
  useEffect(() => {
    if (!SITEWIDE_PATH(path)) return undefined;
    let active = true;
    getDoc(doc(db, "site_seo_pages", encodeURIComponent(path))).then((snapshot) => {
      const value = snapshot.data();
      if (active && snapshot.exists() && value.status === "approved") setSiteSeo(value);
    }).catch(() => {});
    return () => { active = false; };
  }, [path]);
  const resolvedTitle = siteSeo?.title || title;
  const resolvedDescription = siteSeo?.description || description;
  const resolvedImage = siteSeo?.image || image;
  const canonicalUrl = `https://letsgovote.app${path || ""}`;

  const breadcrumbSchema =
    breadcrumbs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: item.name,
            item: `https://letsgovote.app${item.path}`,
          })),
        }
      : null;

  return (
    <Helmet>
    
      <title>{resolvedTitle}</title>

      <meta name="description" content={resolvedDescription} />

      {keywords && <meta name="keywords" content={keywords} />}

      <meta
        name="robots"
        content={noindex ? "noindex,nofollow" : "index,follow"}
      />

      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:site_name" content="Let's Go Vote" />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={resolvedImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={resolvedImage} />

      <meta name="msvalidate.01" content="C9B26A8B261B5B13002E0562DA597102" />
      
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SeoHead;
